import React from 'react';
import DataTable, { Column } from '@/components/common/DataTable';
import Pagination from '@/components/common/Pagination';
import { DateRangePicker } from '@/components/common/DateRangePicker';
import { useEventListing } from './hooks/useEventListing';
import dayjs from 'dayjs';
import { ToggleButton } from '@/components/common/Toggle';
import CustomSelect from '@/components/common/CustomSelect';
import ResultTabs from './components/ResultTabs';
import ResultModal from './components/ResultModal';
import CancelConfirmModal from './components/CancelConfirmModal';
import RollbackConfirmModal from './components/RollbackConfirmModal';
import { PendingMarketItem } from '@/types/pendingMarket';

const EventListingPage: React.FC = () => {
  const [searchText, setSearchText] = React.useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
  const [cancelTarget, setCancelTarget] = React.useState<PendingMarketItem | null>(null);
  const {
    items,
    totalItems,
    isTableLoading,
    page,
    limit,
    setPage,
    setLimit,
    dateRange,
    setDateRange,
    debouncedSearch,
    open,
    setOpen,
    selectedEventName,
    lastResult,
    marketNameLabel,
    runners,
    marketType,
    marketName,
    handleOpenResult,
    handleResultAction,
    betResultLoading,
    rollbackConfirmOpen,
    setRollbackConfirmOpen,

    isPollingEnabled,
    setIsPollingEnabled,
    // runnersLoading,

    // FILTERS
    sport,
    // competitionId,
    // eventId,
    sportOptions,
    // competitionOptions,
    // eventOptions,
    handleSportChange,
    // handleCompetitionChange,
    // setEventId,
    // competitionLoading,
    // eventsLoading,
    activeResultTab,
    setActiveResultTab,
  } = useEventListing();

  const columns: Column[] = [
    { key: 'eventId', label: 'Event ID', className: 'font-semibold' },
    {
      key: 'placedAt',
      label: 'Placed At',
      render: (val: string) => (val ? dayjs(val).format('DD/MM/YYYY, h:mm A') : '-'),
    },
    { key: 'selection', label: 'Selection' },
    ...(activeResultTab === 'rollback'
      ? [
          {
            key: 'result',
            label: 'Last Result',
            render: (value: string) => (value == '-999' ? 'Cancelled' : value || '-'),
          },
        ]
      : []),
    { key: 'marketName', label: 'Market Name' },
    { key: 'eventName', label: 'Events' },
    {
      key: 'result',
      label: 'Result',
      render: (_, row) => (
        <button
          onClick={() => handleOpenResult(row)}
          className={`text-white px-4 py-1 rounded-md bg-primary hover:bg-primary-dark 
            ${activeResultTab === 'rollback' && 'bg-yellow-500 hover:bg-yellow-600'}`}
        >
          {activeResultTab === 'rollback' ? 'Rollback' : 'Result'}
        </button>
      ),
    },
    { key: 'marketType', label: 'Market Type' },
    ...(activeResultTab !== 'rollback'
      ? [
          {
            key: 'cancel',
            label: 'Cancel',
            render: (_: any, row: any) => (
              <button
                onClick={() => {
                  setCancelTarget(row);
                  setIsCancelModalOpen(true);
                }}
                className="text-white px-4 py-1 rounded-md bg-red-600 hover:bg-red-700"
              >
                Cancel
              </button>
            ),
          },
        ]
      : []),
  ];
  const currentAction = activeResultTab === 'pending' ? 'declare' : 'rollback';

  return (
    <div className="py-4 px-2 md:px-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-3">
        <div className="bg-green-shade text-white py-2 px-3 rounded-md min-w-[100px] text-center">
          <div className="text-3xl font-bold">{totalItems}</div>
          <div className="text-sm">
            {activeResultTab === 'pending' ? 'Pending Market Results' : 'Rollback Market Results'}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search Here ..."
              value={searchText}
              className="border border-primary px-3 py-2 pr-8 rounded-md text-sm w-full"
              onChange={(e) => {
                const value = e.target.value;
                setSearchText(value);
                debouncedSearch(value);
              }}
            />

            {searchText && (
              <button
                type="button"
                onClick={() => {
                  setSearchText('');
                  debouncedSearch('');
                  setPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            )}
          </div>

          <DateRangePicker
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex items-center gap-x-4 my-2.5">
        {/* SPORT FILTER */}
        <div className="min-w-[200px]">
          <CustomSelect
            value={sport}
            options={sportOptions}
            onChange={handleSportChange}
            placeholder="Select Sport"
          />
        </div>

        {/* COMPETITION FILTER */}
        {/* <div className="min-w-[240px]">
          <CustomSelect
            value={competitionId}
            options={competitionOptions}
            onChange={handleCompetitionChange}
            placeholder="Select Competition"
            disabled={sport === 'all'}
            isLoading={competitionLoading}
          />
        </div> */}

        {/* EVENT FILTER */}
        {/* <div className="min-w-[280px]">
          <CustomSelect
            value={eventId}
            options={eventOptions}
            onChange={setEventId}
            placeholder="Select Event"
            disabled={sport === 'all' || !competitionId}
            isLoading={eventsLoading}
          />
        </div> */}
        <div className="flex-1 self-center flex justify-end">
          <ToggleButton
            enabled={isPollingEnabled}
            onToggle={() => setIsPollingEnabled((prev) => !prev)}
            label="Auto-refresh"
          />
        </div>
      </div>

      <ResultTabs value={activeResultTab} onChange={setActiveResultTab} />
      {/* TABLE */}
      <div className="p-2 bg-white rounded-lg">
        <DataTable columns={columns} data={items} isLoading={isTableLoading} />

        <Pagination
          totalItems={totalItems}
          currentPage={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          isLoading={isTableLoading}
        />
      </div>

      {/* RESULT MODAL */}
      {activeResultTab === 'pending' ? (
        <ResultModal
          open={open}
          onClose={() => setOpen(false)}
          eventName={selectedEventName}
          marketType={marketType}
          marketName={marketName}
          runners={runners}
          isLoading={betResultLoading}
          onSave={(data, runs) => handleResultAction(currentAction, data, runs)}
        />
      ) : (
        <RollbackConfirmModal
          open={rollbackConfirmOpen}
          onClose={() => setRollbackConfirmOpen(false)}
          eventName={selectedEventName}
          marketName={marketNameLabel}
          lastResult={lastResult}
          isLoading={betResultLoading}
          onConfirm={async () => {
            const ok = await handleResultAction(currentAction, {}, '');
            if (ok) {
              setRollbackConfirmOpen(false);
            }
          }}
        />
      )}

      <CancelConfirmModal
        open={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setCancelTarget(null);
        }}
        eventName={cancelTarget?.eventName}
        marketName={cancelTarget?.marketName}
        isLoading={betResultLoading}
        onConfirm={async () => {
          if (!cancelTarget) return;
          const ok = await handleResultAction(currentAction, {}, '', {
            isCancel: true,
            eventDetails: cancelTarget,
          });
          if (ok) {
            setIsCancelModalOpen(false);
            setCancelTarget(null);
          }
        }}
      />
    </div>
  );
};

export default EventListingPage;
